import { NextRequest, NextFetchEvent, NextResponse, NextMiddleware } from 'next/server';

import { logger } from '../utils/logger';
import { generateCookieDomain } from '../utils/cookies/generateCookieDomain';
import { generateCookieExpire } from '../utils/cookies/generateCookieExpire';

import { ConfigurableMiddlewareFactory, MiddlewareConfig, MiddlewareFactory } from './type';
import fetchWebviewAccessToken from '../apis/token/webview';
import { isPathMatching } from '../utils/path';
import { parseAppInfoFromUA } from '../utils';

interface BizLog {
  [key: string]: unknown;
  pid: string;
}

const APP_VERSION_HEADER_NAME = 'mrt-app-version';
const APP_VERSION_COOKIE_NAME = 'app-version';
const DEVICE_KEY_HEADER_NAME = 'device-key';
const DEVICE_KEY_COOKIE_NAME = 'device-key';
const MRT_BIZ_LOG_HEADER_NAME = 'mrt_biz_log';
const PID_COOKIE_NAME = 'pid';
const API_ACCESS_TOKEN_NAME = 'api-access-token';

function parseBizLog(bizLog: string | null): BizLog | null {
  if (!bizLog) {
    return null;
  }

  try {
    const decodedBizLog = atob(bizLog);
    return JSON.parse(decodedBizLog);
  } catch (error) {
    return null;
  }
}

function burnCookies(req: NextRequest, res: NextResponse) {
  const headerToCookieList = [
    {
      headerKey: APP_VERSION_HEADER_NAME,
      cookieKey: APP_VERSION_COOKIE_NAME,
    },
    {
      headerKey: DEVICE_KEY_HEADER_NAME,
      cookieKey: DEVICE_KEY_COOKIE_NAME,
    },
  ];
  const { pid = null } = parseBizLog(req.headers.get(MRT_BIZ_LOG_HEADER_NAME)) ?? {};

  if (pid) {
    res.cookies.set(PID_COOKIE_NAME, pid, {
      domain: generateCookieDomain(),
      sameSite: 'none',
      secure: true,
      expires: generateCookieExpire(1, 'years'),
    });
  }

  headerToCookieList.forEach(({ headerKey, cookieKey }) => {
    const cookieValue = req.headers.get(headerKey);

    if (!cookieValue) {
      return;
    }

    res.cookies.set(cookieKey, cookieValue, {
      domain: generateCookieDomain(),
      sameSite: 'none',
      secure: true,
      expires: generateCookieExpire(1, 'years'),
    });
  });
}

// 기본 webview 미들웨어 로직
const handleWebviewRequest = async (
  req: NextRequest,
  event: NextFetchEvent,
  next: NextMiddleware,
  response: NextResponse
) => {
  const appVesrionFromUA = parseAppInfoFromUA(req.headers.get('user-agent') ?? null);
  const isWebView =
    req.headers.has(APP_VERSION_HEADER_NAME) ||
    req.cookies.has(APP_VERSION_COOKIE_NAME) ||
    Boolean(appVesrionFromUA);

  if (!isWebView) {
    return next(req, event);
  }

  // 1. 네이티브에서 전달한 헤더를 쿠키로 저장한다.
  burnCookies(req, response);

  // 2. 토큰이 존재하는 경우 검증/재발급 한다.
  const apiAccessToken =
    req.cookies.get(API_ACCESS_TOKEN_NAME)?.value ?? req.headers.get(API_ACCESS_TOKEN_NAME);
  const deviceKey =
    req.headers.get(DEVICE_KEY_HEADER_NAME) ?? req.cookies.get(DEVICE_KEY_COOKIE_NAME)?.value;

  if (!apiAccessToken || !deviceKey) {
    return next(req, event);
  }

  try {
    const {
      data: { apiAccessToken: newApiAccessToken },
    } = await fetchWebviewAccessToken({ apiAccessToken, deviceKey });

    if (newApiAccessToken) {
      response.cookies.set(API_ACCESS_TOKEN_NAME, newApiAccessToken, {
        domain: generateCookieDomain(),
        sameSite: 'none',
        secure: true,
        expires: generateCookieExpire(1, 'years'),
      });
    }
  } catch (error) {
    logger.error(error);

    response.cookies.delete(API_ACCESS_TOKEN_NAME);

    const encodedRedirectUrl = encodeURIComponent(req.url);

    return NextResponse.redirect(`mrt://signin?return_to=${encodedRedirectUrl}`);
  }

  return next(req, event);
};

const createConfiguredMiddleware = (config: MiddlewareConfig): MiddlewareFactory => {
  return (next: NextMiddleware, response: NextResponse) => {
    return async (req: NextRequest, event: NextFetchEvent) => {
      if (
        isPathMatching(req.nextUrl.pathname, {
          excludePaths: config.excludePaths,
          includePaths: config.includePaths,
        })
      ) {
        return handleWebviewRequest(req, event, next, response);
      }
      return next(req, event);
    };
  };
};

const createDefaultMiddleware = (next: NextMiddleware, response: NextResponse): NextMiddleware => {
  return async (req: NextRequest, event: NextFetchEvent) => {
    return handleWebviewRequest(req, event, next, response);
  };
};

export const webviewMiddleware: ConfigurableMiddlewareFactory<MiddlewareConfig> = ((
  configOrNextMiddleware: NextMiddleware | MiddlewareConfig,
  response?: NextResponse
): NextMiddleware | MiddlewareFactory => {
  if (
    !response &&
    typeof configOrNextMiddleware === 'object' &&
    typeof configOrNextMiddleware !== 'function'
  ) {
    return createConfiguredMiddleware(configOrNextMiddleware as MiddlewareConfig);
  }

  return createDefaultMiddleware(configOrNextMiddleware as NextMiddleware, response!);
}) as ConfigurableMiddlewareFactory<MiddlewareConfig>;
